<template>
  <div>
    <v-form @submit.prevent="submitResponse">
      <p class="mb-3">
        En utilisant les données dans le tableau ci-dessous, répondez à chacune des questions
        ci-dessous:
      </p>

      <p class="mb-5 pl-8">
        <chemical-latex content="3A + B -> C" />
      </p>

      <table class="table table-sm table-hover border mb-4" style="width: 400px">
        <thead class="thead-dark text-center br">
          <th>
            <stemble-latex content="$\text{Essai}$" />
          </th>
          <th>
            <stemble-latex content="$\text{[A] (M)}$" />
          </th>
          <th>
            <stemble-latex content="$\text{[B] (M)}$" />
          </th>
          <th>
            <stemble-latex content="$\text{Vitesse Initiale (M/s)}$" />
          </th>
        </thead>
        <tbody>
          <tr>
            <td style="vertical-align: top; text-align: center">
              <stemble-latex content="$1$" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <number-value :value="Aconc1" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <number-value :value="Bconc1" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <latex-number :number="rate1.toFixed(5)" />
            </td>
          </tr>
          <tr>
            <td style="vertical-align: top; text-align: center">
              <stemble-latex content="$2$" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <number-value :value="Aconc1" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <latex-number :number="Bconc2.toFixed(3)" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <latex-number :number="rate2.toFixed(5)" />
            </td>
          </tr>
          <tr>
            <td style="vertical-align: top; text-align: center">
              <stemble-latex content="$3$" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <latex-number :number="Aconc3.toFixed(3)" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <latex-number :number="Bconc2.toFixed(3)" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <latex-number :number="rate3.toFixed(5)" />
            </td>
          </tr>
          <tr>
            <td style="vertical-align: top; text-align: center">
              <stemble-latex content="$4$" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <latex-number :number="Aconc3.toFixed(3)" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <number-value :value="Bconc1" />
            </td>
            <td style="vertical-align: top; text-align: center">
              <latex-number :number="rate4.toFixed(5)" />
            </td>
          </tr>
        </tbody>
      </table>

      <p class="mb-n2">a) Quel est l'ordre partiel de la réaction par rapport au réactif A?</p>

      <v-radio-group v-model="inputs.orderA" class="pl-8" :disabled="!allowEditing">
        <v-radio
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :label="option.condition"
        />
      </v-radio-group>

      <p class="mb-n2">b) Quel est l'ordre partiel de la réaction par rapport au réactif B?</p>

      <v-radio-group v-model="inputs.orderB" class="pl-8" :disabled="!allowEditing">
        <v-radio
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :label="option.condition"
        />
      </v-radio-group>

      <p>c) Quelle est la constante de vitesse de la réaction ?</p>

      <calculation-input
        v-model="inputs.rateConstantAnswer"
        prepend-text="$k:$"
        append-text="$\text{Unités non fournies}$"
        :disabled="!allowEditing"
      />
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import ChemicalLatex from '../displayers/ChemicalLatex';
import LatexNumber from '../displayers/LatexNumber';
import CalculationInput from '../inputs/CalculationInput';
import NumberValue from '../NumberValue';
import StembleLatex from '@/tasks/components/StembleLatex';

export default {
  name: 'Question151Fr',
  components: {
    StembleLatex,
    ChemicalLatex,
    LatexNumber,
    CalculationInput,
    NumberValue,
  },
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        orderA: null,
        orderB: null,
        rateConstantAnswer: null,
      },
      options: [
        {condition: 'Ordre nul', value: '0'},
        {condition: 'Premier Ordre', value: '1'},
        {condition: 'Deuxième Ordre', value: '2'},
        {condition: 'Troisième Ordre', value: '3'},
      ],
    };
  },
  computed: {
    Aconc1() {
      return this.taskState.getValueBySymbol('Aconc1').content;
    },
    Bconc1() {
      return this.taskState.getValueBySymbol('Bconc1').content;
    },
    scaleA() {
      return this.taskState.getValueBySymbol('scaleA').content;
    },
    scaleB() {
      return this.taskState.getValueBySymbol('scaleB').content;
    },
    orderA() {
      return this.taskState.getValueBySymbol('orderA').content.value;
    },
    orderB() {
      return this.taskState.getValueBySymbol('orderB').content.value;
    },
    rateConstant() {
      return this.taskState.getValueBySymbol('rateConstant').content;
    },
    rate1() {
      return (
        this.rateConstant.toFloat() *
        this.Aconc1.toFloat() ** this.orderA *
        this.Bconc1.toFloat() ** this.orderB
      );
    },
    Bconc2() {
      return this.Bconc1.toFloat() * this.scaleB.toFloat();
    },
    rate2() {
      return (
        this.rateConstant.toFloat() *
        this.Aconc1.toFloat() ** this.orderA *
        this.Bconc2 ** this.orderB
      );
    },
    Aconc3() {
      return this.Aconc1.toFloat() * this.scaleA.toFloat();
    },
    rate3() {
      return this.rateConstant.toFloat() * this.Aconc3 ** this.orderA * this.Bconc2 ** this.orderB;
    },
    rate4() {
      return (
        this.rateConstant.toFloat() *
        this.Aconc3 ** this.orderA *
        this.Bconc1.toFloat() ** this.orderB
      );
    },
  },
};
</script>
